import {
  EFileUploadStatus,
  type IFileUploadEntry,
} from "@frontend-shared/types";
import { isCancel } from "axios";
import { useCallback, useEffect, useRef, useState } from "react";

import { extractServerErrorMessage } from "@/api/utils";

interface IUploadExtras {
  signal: AbortSignal;
  onProgress: (percent: number) => void;
}

export interface IUseTrackedFileUploadConfig<
  TParams,
  TEntry extends IFileUploadEntry,
> {
  // NOTE: The actual network call. Wired with a fresh `AbortSignal` and an
  // `onProgress` callback per upload — implementations only need to forward
  // them to axios (`signal` + `onUploadProgress`) and return on success.
  // NOTE: Return type is `Promise<unknown>` so callers can pass through
  // axios responses (or `Promise<TResponse>`) without wrapping. The shared
  // hook discards the resolved value — consumers that need it should hook
  // into `onSuccess` instead.
  upload: (params: TParams, extras: IUploadExtras) => Promise<unknown>;
  // NOTE: Builds the initial entry. Caller chooses the entry shape (e.g.
  // `IFileUploadEntry` for knowledge, `ILogoUploadEntry` with `mode` for
  // logo) — `TEntry extends IFileUploadEntry` so the shared machinery only
  // ever updates the base fields (status / progress / errorMessage).
  buildEntry: (params: TParams) => TEntry;
  // NOTE: Optional. Fires once after the upload settles successfully (after
  // status → DONE). Use for `queryClient.invalidateQueries`, toasts, cache
  // busters, etc.
  onSuccess?: (params: TParams) => void;
  // NOTE: Localized fallback message when the server response has no
  // `message`. Function form lets callers re-resolve through `t()` on each
  // failure (i18n key may be picked at upload-time language).
  errorFallback: string | (() => string);
}

// NOTE: Tracked-upload state machine shared by every file-upload site that
// shows per-file progress + cancel + DONE/ERROR persistence:
//
//   - Knowledge files (org)
//   - Admin Knowledge files
//   - Logos (admin + org, per light/dark dropzone)
//
// Each consumer hook provides the network call, the entry shape (some sites
// extend `IFileUploadEntry` with mode-specific fields), and success
// side-effects via `IUseTrackedFileUploadConfig`. This hook owns the
// `useState`/`useRef` plumbing and the upload-lifecycle (UPLOADING → DONE /
// ERROR / removed-on-cancel) so we don't duplicate that across sites.
//
// Notably distinct from `useUploadVstrAudioFile` which has a CONFIRMING
// phase (presign → S3 PUT → backend confirm) — that flow is left untouched.
export const useTrackedFileUpload = <
  TParams,
  TEntry extends IFileUploadEntry = IFileUploadEntry,
>(
  config: IUseTrackedFileUploadConfig<TParams, TEntry>,
) => {
  const [uploads, setUploads] = useState<TEntry[]>([]);
  const abortControllers = useRef(new Map<string, AbortController>());

  // NOTE: Stable ref to the latest config so `uploadFile` doesn't change
  // identity every render — keeps `useCallback` consumers stable.
  const configRef = useRef(config);
  useEffect(() => {
    configRef.current = config;
  });

  const updateUpload = useCallback(
    (id: string, update: Partial<IFileUploadEntry>): void => {
      setUploads((prev) =>
        prev.map((entry) =>
          entry.id === id ? { ...entry, ...update } : entry,
        ),
      );
    },
    [],
  );

  const removeUpload = useCallback((id: string): void => {
    setUploads((prev) => prev.filter((entry) => entry.id !== id));
  }, []);

  const cancelUpload = useCallback((id: string): void => {
    abortControllers.current.get(id)?.abort();
  }, []);

  const cancelAllUploads = useCallback((): void => {
    for (const controller of abortControllers.current.values()) {
      controller.abort();
    }
  }, []);

  const uploadFile = useCallback(
    async (params: TParams): Promise<void> => {
      const cfg = configRef.current;
      const entry = cfg.buildEntry(params);
      const abortController = new AbortController();
      abortControllers.current.set(entry.id, abortController);

      setUploads((prev) => [...prev, entry]);

      try {
        await cfg.upload(params, {
          signal: abortController.signal,
          onProgress: (percent) =>
            updateUpload(entry.id, { progress: percent }),
        });
        updateUpload(entry.id, {
          status: EFileUploadStatus.DONE,
          progress: 100,
        });
        cfg.onSuccess?.(params);
      } catch (error) {
        // NOTE: axios surfaces aborts as `CanceledError`. `isCancel` is the
        // documented detector and also covers fetch-style `AbortError` if
        // the underlying adapter ever changes.
        if (isCancel(error)) {
          removeUpload(entry.id);
          return;
        }
        const fallback =
          typeof cfg.errorFallback === "function"
            ? cfg.errorFallback()
            : cfg.errorFallback;
        const errorMessage = extractServerErrorMessage(error) ?? fallback;
        updateUpload(entry.id, {
          status: EFileUploadStatus.ERROR,
          errorMessage,
        });
      } finally {
        abortControllers.current.delete(entry.id);
      }
    },
    [updateUpload, removeUpload],
  );

  return {
    uploads,
    uploadFile,
    cancelUpload,
    cancelAllUploads,
    removeUpload,
  };
};
